import { useDispatch, useSelector } from "react-redux";
import {
  clearCachedLeafletData,
  initializeEnvironment,
  registerTranslatedEnvironmentContent,
  selectCurrentEnvironmentId,
  selectTranslatedEnvironmentContent,
  setEnvironmentMetadata,
} from "../app/slices/EnvironmentSlice";
import { setInteractionPickerContext } from "../app/slices/InteractionPickerSlice";
import {
  initializeCharacterPresets,
  initializeOutfits,
} from "../app/slices/MetadataSlice";
import {
  loadActors,
  loadDisableProgressBar,
  loadSelectedEnvironmentLayerIds,
  loadPropPresets,
  loadSequenceVariables,
  hydrateTakes,
  TrainingType,
} from "../app/slices/TrainingSlice";
import useProjectMigration3D from "../features/project_migration/ProjectMigration3DHook";
import { Actor, normalizeActors } from "../models/Actor";
import { CharacterPreset } from "../models/CharacterPreset";
import { collectOutfitNames, Outfit } from "../models/Outfit";
import Take3D from "../models/Take3D";
import { Training } from "../models/Training";
import { EnvironmentService } from "../services/EnvironmentService";
import { MetadataService } from "../services/MetadataService";
import { OutfitService } from "../services/OutfitService";
import { Prop, PropType } from "../models/Prop";
import { PropService } from "../services/PropService";
import { UsedProp } from "../models/UsedProp";
import { addUsedProps } from "../app/slices/PropLibrarySlice";
import UsePropLibrary from "../features/prop_library/PropLibrary.hooks";
import { SituationService } from "../services/SituationService";
import {
  loadEnvironmentPropsFailure,
  loadEnvironmentPropsStart,
  loadEnvironmentPropsSuccess,
} from "../app/slices/EnvironmentPropSlice";
import { PropDetailsDTO } from "../dto/PropDetailsDTO";
import {
  logPrefabNameChanges,
  reconcileTrainingProps,
} from "../helpers/PrefabNameReconciler";

const ENVIRONMENT_PROP_TYPE = 0;

export default function useLoadTraining3DContent() {
  const dispatch = useDispatch();
  const targetVersion = Number(process.env.REACT_APP_PLAYIT_TRAINING_VERSION);
  const { migrateProject } = useProjectMigration3D();

  const cachedEnvironmentContent = useSelector(
    selectTranslatedEnvironmentContent
  );
  const currentEnvironmentId = useSelector(selectCurrentEnvironmentId);

  const { loadCategories, loadInteractions } = UsePropLibrary();

  const loadTraining3DContent = async (
    training: Training,
    token: string,
    orgId: string
  ): Promise<string[]> => {
    var envId = training.metadata?.["environmentId"];
    if (!envId) return []; // something must be wrong

    // clear leaflet cache
    if (envId !== currentEnvironmentId) {
      dispatch(clearCachedLeafletData());
    }

    // load prop categories
    await loadCategories();
    // load prop interactions
    await loadInteractions();

    const environmentContent = await getEnvironmentContent(orgId, token, envId);

    if (!environmentContent) return [];

    var content = await EnvironmentService.GetEnvironmentContent(
      environmentContent.metadataUri
    );

    if (!content) return [];

    // environment vars
    dispatch(
      initializeEnvironment({
        id: environmentContent.id,
        name: environmentContent.name,
        floorplanUri: environmentContent.floorplanUri,
        gameEnvironment: environmentContent.gameEnvironment,
        bounds: {
          x: content.bounds?.x ?? 32,
          y: content.bounds?.y ?? 32,
        },
      })
    );

    console.log("content");
    console.log(content);
    dispatch(
      setEnvironmentMetadata({
        take_positions: content.take_positions ?? [],
        layers: content.layers ?? [],
      })
    );

    await loadEnvironmentProps(orgId, token, envId);

    // loading only the outfits this training uses; the pickers page in the rest
    var outfits: Outfit[] = await OutfitService.GetOutfitsByName(
      orgId,
      collectOutfitNames(training)
    );
    dispatch(initializeOutfits(outfits));

    // loading character presets
    var presets: CharacterPreset[] =
      await MetadataService.GetCharacterPresets();
    dispatch(initializeCharacterPresets(presets));

    // actors
    var actors: Actor[] = normalizeActors(training.metadata?.["actors"]);
    dispatch(loadActors(actors));

    // load used prop content from the backend first — it is the source of
    // truth for prefab names, which the presets below get reconciled against
    var props: Prop[] = training.metadata?.["props"] ?? [];
    const usedProps = await loadUsedPropsContent(props, orgId, token);

    // sequence variables
    var sequenceVariables = training.metadata?.["sequenceVariables"];
    dispatch(loadSequenceVariables(sequenceVariables ?? []));

    // project settings
    dispatch(
      loadDisableProgressBar(
        training.metadata?.["disableProgressBar"] === true
      )
    );
    const selectedLayerIds = training.metadata?.["selectedLayerIds"];
    const normalizedSelectedLayerIds = Array.isArray(selectedLayerIds)
      ? selectedLayerIds.filter((id): id is string => typeof id === "string")
      : [];
    dispatch(loadSelectedEnvironmentLayerIds(normalizedSelectedLayerIds));

    // takes
    var takes: Take3D[] = training.takes as Take3D[];

    // migration
    if (training.version < targetVersion) {
      takes = migrateProject(training.version, targetVersion, takes, actors);
    }

    // Animation references gain their API `animationId` inside `hydrateTakes`
    // (normalizeTake3D -> ensureAnimationIds), which also covers Unity
    // re-imports and take copies. No API call is needed for that.

    // reconcile stale prefab names against the fetched backend metadata,
    // before the hydrate dispatches so no dirty/undo state is produced
    const {
      presets: reconciledPresets,
      takes: reconciledTakes,
      changes,
    } = reconcileTrainingProps(props, takes ?? [], usedProps);
    logPrefabNameChanges(changes, "load");

    dispatch(loadPropPresets(reconciledPresets));

    // set takes
    dispatch(hydrateTakes(reconciledTakes));

    // interaction picker context
    dispatch(setInteractionPickerContext(TrainingType.Training3D));

    return Array.from(
      new Set(
        usedProps.flatMap((usedProp) => usedProp.metadata.localizedKeys ?? [])
      )
    );
  };

  const getEnvironmentContent = async (
    orgId: string,
    token: string,
    envId: string
  ) => {
    if (cachedEnvironmentContent[envId] !== undefined) {
      return cachedEnvironmentContent[envId];
    } else {
      const environmentContent =
        await EnvironmentService.GetTranslatedEnvironment(
          orgId,
          token,
          envId,
          "en",
          "US"
        );

      if (environmentContent)
        dispatch(registerTranslatedEnvironmentContent(environmentContent));

      return environmentContent;
    }
  };

  const loadUsedPropsContent = async (
    props: Prop[],
    orgId: string,
    token: string
  ): Promise<UsedProp[]> => {
    if (!props) return [];

    const propIds = new Set<string>();
    const situationIds: string[] = [];

    props.forEach((prop) => {
      if (
        prop.propType === PropType.Default ||
        prop.propType === PropType.Environment
      ) {
        propIds.add(prop.propId);
      }
      if (prop.propType === PropType.Situation) situationIds.push(prop.propId);
    });

    // per prop, content -> metadata stays sequential (the metadata uri comes
    // from the content), but the props themselves are fetched in parallel
    const regularResults = await Promise.all(
      Array.from(propIds).map(async (propId): Promise<UsedProp | null> => {
        const content = await PropService.GetPropContent(orgId, token, propId);
        if (!content) return null;
        const metadata = await PropService.GetPropMetadata(content.metadataUri);
        if (!metadata) return null;
        return {
          propContent: content,
          metadata: {
            ...metadata,
            hideInPropLibrary: content.hideInPropLibrary ?? false,
          },
        };
      })
    );

    const situationResults = await Promise.all(
      situationIds.map(async (situationId): Promise<UsedProp | null> => {
        const content = await SituationService.GetSituationsContent(
          orgId,
          token,
          situationId
        );
        if (!content) return null;
        const metadata = await SituationService.GetSituationMetadata(
          content.metadataUri
        );
        if (!metadata) return null;
        return { propContent: content, metadata: metadata };
      })
    );

    const usedProps = [...regularResults, ...situationResults].filter(
      (usedProp): usedProp is UsedProp => usedProp !== null
    );

    dispatch(addUsedProps(usedProps));
    return usedProps;
  };

  const loadEnvironmentProps = async (
    orgId: string,
    token: string,
    environmentId: string
  ) => {
    dispatch(loadEnvironmentPropsStart({ environmentId }));

    try {
      const placements = await PropService.GetEnvironmentProps(
        orgId,
        token,
        environmentId
      );
      const details = (
        await Promise.all(
          placements.map((placement) =>
            PropService.GetPropDetails(
              orgId,
              token,
              ENVIRONMENT_PROP_TYPE,
              placement.propId
            )
          )
        )
      ).filter((detail): detail is PropDetailsDTO => !!detail);

      dispatch(
        loadEnvironmentPropsSuccess({
          environmentId,
          placements,
          details,
        })
      );
    } catch (error) {
      dispatch(
        loadEnvironmentPropsFailure({
          environmentId,
          error: error instanceof Error ? error.message : "Unknown error",
        })
      );
    }
  };

  return { loadTraining3DContent } as const;
}
